'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ServiceAreaMapProps {
  currentLanguage: 'fr' | 'de';
}

const ServiceAreaMap = ({ currentLanguage }: ServiceAreaMapProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const content = {
    fr: {
      title: "Zone de service",
      subtitle: "Nous servons Le Locle et les régions environnantes",
      mainLocation: "Bureau principal - Le Locle",
      address: "Rue de Gerardmer 24, 2400 Le Locle, Suisse",
      serviceAreas: [
        "Le Locle",
        "La Chaux-de-Fonds",
        "Les Brenets",
        "Le Cerneux-Péquignot",
        "La Brévine",
        "Les Ponts-de-Martel"
      ],
      coverage: "Rayon de service: 100 km",
      getDirections: "Obtenir l\'itinéraire"
    },
    de: {
      title: "Servicebereich",
      subtitle: "Wir bedienen Le Locle und die umliegenden Regionen",
      mainLocation: "Hauptbüro - Le Locle",
      address: "Rue de Gerardmer 24, 2400 Le Locle, Schweiz",
      serviceAreas: [
        "Le Locle",
        "La Chaux-de-Fonds",
        "Les Brenets",
        "Le Cerneux-Péquignot",
        "La Brévine",
        "Les Ponts-de-Martel"
      ],
      coverage: "Serviceradius: 100 km",
      getDirections: "Route abrufen"
    }
  };

  const text = content[currentLanguage];

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="h-96 bg-muted rounded-2xl animate-pulse"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              {text.title}
            </h2>
            <p className="text-lg text-muted-foreground">
              {text.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Map */}
            <div className="lg:col-span-2">
              <div className="bg-card rounded-2xl shadow-lg overflow-hidden h-[500px]">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title="Rouissi Services Location"
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps?q=47.0571,6.7491&z=14&output=embed"
                  className="border-0"
                />
              </div>
            </div>

            {/* Location Info */}
            <div className="space-y-6">
              <div className="bg-card rounded-2xl shadow-lg p-6">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg flex-shrink-0">
                    <Icon name="MapPinIcon" size={24} variant="solid" className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                      {text.mainLocation}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {text.address}
                    </p>
                  </div>
                </div>

                <a
                  href="https://maps.google.com/?q=47.0571,6.7491"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary hover:text-secondary font-medium transition-colors duration-300"
                >
                  <Icon name="ArrowTopRightOnSquareIcon" size={20} variant="outline" />
                  <span>{text.getDirections}</span>
                </a>
              </div>

              <div className="bg-card rounded-2xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Icon name="MapIcon" size={24} variant="outline" className="text-primary" />
                  <h3 className="text-lg font-heading font-bold text-foreground">
                    {text.coverage}
                  </h3>
                </div>

                <div className="space-y-2">
                  {text.serviceAreas.map((area, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 text-sm text-foreground"
                    >
                      <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success" />
                      <span>{area}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreaMap;